import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, AlertTriangle, X } from 'lucide-react';
import toast from 'react-hot-toast';

const CustomToast = ({ t }) => {
  // Basic icon map for fallback and compatibility
  const iconMap = {
    success: <CheckCircle className="w-6 h-6" />,
    error: <XCircle className="w-6 h-6" />,
    warning: <AlertTriangle className="w-6 h-6" />,
    info: <AlertCircle className="w-6 h-6" />,
  };

  // Sophisticated color scheme with matching gradients
  const designTokens = {
    success: {
      background: 'bg-gradient-to-r from-[#f9fefb] to-[#eef9f3]',
      iconBg: 'bg-gradient-to-br from-emerald-500 to-primary-600',
      border: 'border-[#d7efe2]',
      textHeading: 'text-[#0e6e52]',
      text: 'text-[#2b7b65]',
      glowColor: 'rgba(0, 119, 100, 0.2)',
      iconColor: 'text-white',
      progressColor: '#15b585'
    },
    error: {
      background: 'bg-gradient-to-r from-[#fefafa] to-[#fdf0f0]',
      iconBg: 'bg-gradient-to-br from-[#e93d55] to-[#c4184c]',
      border: 'border-[#f5dada]',
      textHeading: 'text-[#c4184c]',
      text: 'text-[#c84263]',
      glowColor: 'rgba(196, 24, 76, 0.2)',
      iconColor: 'text-white',
      progressColor: '#e93d55'
    },
    warning: {
      background: 'bg-gradient-to-r from-[#fffefb] to-[#fef8ea]',
      iconBg: 'bg-gradient-to-br from-[#fbb245] to-[#ed8d13]',
      border: 'border-[#fce7c0]',
      textHeading: 'text-[#b76e0e]',
      text: 'text-[#b3760a]',
      glowColor: 'rgba(237, 141, 19, 0.2)',
      iconColor: 'text-white',
      progressColor: '#fbb245'
    },
    info: {
      background: 'bg-gradient-to-r from-[#fafbfe] to-[#f0f3fd]',
      iconBg: 'bg-gradient-to-br from-[#3d87e9] to-[#1b57e2]',
      border: 'border-[#dae3f5]',
      textHeading: 'text-[#1b57e2]',
      text: 'text-[#4264c8]',
      glowColor: 'rgba(27, 87, 226, 0.2)',
      iconColor: 'text-white',
      progressColor: '#3d87e9'
    }
  };

  // Default to error type if not recognized for better error handling
  const toastType = t.type && ['success', 'error', 'warning', 'info'].includes(t.type) ? t.type : 'error';
  const tokens = designTokens[toastType];

  // Progress bar animation
  const duration = 5; // seconds
  
  // Enhanced animation variants with more sophisticated physics
  const containerVariants = {
    initial: { 
      opacity: 0, 
      y: -30,
      scale: 0.92,
      filter: 'blur(10px)',
      boxShadow: '0 0 0 rgba(0,0,0,0)'
    },
    animate: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      filter: 'blur(0px)',
      boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1), 0 2px 12px -2px rgba(0,0,0,0.05)',
      transition: {
        type: 'spring',
        damping: 30,
        stiffness: 350,
        mass: 1.2,
        when: "beforeChildren",
        staggerChildren: 0.08
      }
    },
    exit: { 
      opacity: 0,
      y: -15,
      scale: 0.95,
      filter: 'blur(6px)',
      transition: { 
        duration: 0.25,
        ease: [0.32, 0.72, 0, 1]
      } 
    }
  };

  const messageVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring",
        damping: 25,
        stiffness: 200
      }
    }
  };

  return (
    <AnimatePresence>
      {t.visible && (
        <motion.div
          layout
          variants={containerVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="relative"
        >
          {/* Main toast container with subtle glass effect */}
          <motion.div
            layout
            className={`${tokens.background} border ${tokens.border} rounded-xl overflow-hidden flex items-stretch relative`}
            style={{ 
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              boxShadow: `0 10px 30px -5px ${tokens.glowColor}, 0 5px 20px -5px rgba(0,0,0,0.05), 0 0 0 1px rgba(255,255,255,0.1) inset`,
            }}
          >
            {/* Icon section with gradient background */}
            <div className={`flex items-center justify-center pl-5`}>
              <div className={`${tokens.iconBg} rounded-full p-2 flex-shrink-0 shadow-lg flex items-center justify-center`} 
                style={{ 
                  boxShadow: `0 0 15px ${tokens.glowColor}`,
                  minWidth: '38px',
                  minHeight: '38px'
                }}
              >
                <div className={tokens.iconColor}>
                  {iconMap[toastType]}
                </div>
              </div>
            </div>
            
            {/* Message content */}
            <motion.div 
              className="flex-grow py-3.5 px-4"
              variants={messageVariants}
            >
              <div className="flex flex-col">
                <span className={`${tokens.textHeading} font-semibold text-sm tracking-tight`}>
                  {toastType.charAt(0).toUpperCase() + toastType.slice(1)}
                </span>
                <div className={`${tokens.text} text-sm mt-0.5 font-normal leading-snug tracking-tight`}>
                  {t.message}
                </div>
              </div>
            </motion.div>
            
            {/* Close button with sophisticated hover animation */}
            <div className="flex items-center pr-4">
              <motion.button
                whileHover={{ 
                  scale: 1.15, 
                  rotate: 90,
                  backgroundColor: 'rgba(0,0,0,0.05)'
                }}
                whileTap={{ scale: 0.9 }}
                transition={{ 
                  duration: 0.15,
                  type: "spring",
                  stiffness: 400
                }}
                onClick={() => toast.dismiss(t.id)}
                className="rounded-full transition-colors duration-200 flex items-center justify-center hover:bg-gray-100/20"
                style={{ 
                  transformOrigin: 'center', 
                  height: '32px', 
                  width: '32px' 
                }}
              >
                <X className="w-5 h-5 text-gray-500" strokeWidth={2.5} />
              </motion.button>
            </div>
            
            {/* Auto-dismissing progress bar that stays within bounds */}
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration, ease: 'linear' }}
              className="absolute bottom-0 left-0 right-0 h-[2px]"
              style={{ 
                background: `linear-gradient(to right, ${tokens.progressColor}CC, ${tokens.progressColor})`,
                boxShadow: `0 0 8px ${tokens.progressColor}80`,
                borderBottomLeftRadius: '0.75rem',
                borderBottomRightRadius: '0.75rem',
              }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CustomToast;